/**
 * Audio comparison on speech/music content, not just overall tone.
 *
 * Each file is reduced to mel-cepstral (MFCC-style) frames, with the file's own average removed so volume,
 * EQ and steady background noise matter little. Frames that are only silence or noise are skipped, and the
 * two files are lined up with dynamic time warping, so a different start time, pauses or small speed
 * differences do not count as differences. The score is how well the matched frames agree, calibrated so that
 * unrelated audio scores low.
 */

const TARGET_RATE = 8000;
const FRAME = 512; // 64 ms at 8 kHz
const N_MEL = 24;
const N_CEP = 13; // cepstral coefficients 1..13
const MAX_SECONDS = 240;
const BAND_SECONDS = 40; // DTW can shift by up to this much

export interface Features {
  frames: Float32Array[]; // one cepstral vector per ACTIVE frame
  times: number[]; // start time (s) of each active frame
  hop: number; // seconds per frame
  seconds: number; // analysed length
  /** Same frames without removing the file's average: loudness-normalised spectral shape, used for steady tones. */
  raw: Float32Array[];
  /** True when the sound barely changes over time (a tone, a hum): the file-average trick would erase it. */
  steady: boolean;
  /** Seconds from the start of the file to the first real sound (leading silence trimmed), 10 ms resolution. */
  onset: number;
}

function fft(re: Float64Array, im: Float64Array) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { [re[i], re[j]] = [re[j], re[i]]; [im[i], im[j]] = [im[j], im[i]]; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1, ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const a = i + k, b = i + k + len / 2;
        const tr = re[b] * cr - im[b] * ci, ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr; im[b] = im[a] - ti;
        re[a] += tr; im[a] += ti;
        const nr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = nr;
      }
    }
  }
}

const hz2mel = (f: number) => 2595 * Math.log10(1 + f / 700);
const mel2hz = (m: number) => 700 * (Math.pow(10, m / 2595) - 1);

function melFilters(rate: number): Array<[number, number, number]> {
  const lo = hz2mel(100), hi = hz2mel(Math.min(3800, rate / 2 - 100));
  const pts = Array.from({ length: N_MEL + 2 }, (_, i) => mel2hz(lo + ((hi - lo) * i) / (N_MEL + 1)));
  const bin = (f: number) => (f * FRAME) / rate;
  return Array.from({ length: N_MEL }, (_, i) => [bin(pts[i]), bin(pts[i + 1]), bin(pts[i + 2])]);
}

/** Mono samples at any rate -> features. */
export function extractFeatures(mono: Float32Array, sampleRate: number): Features {
  // Box-average decimation to about 8 kHz (also a crude low-pass).
  const f = Math.max(1, Math.round(sampleRate / TARGET_RATE));
  const rate = sampleRate / f;
  const maxIn = Math.min(mono.length, Math.floor(MAX_SECONDS * sampleRate));
  const n = Math.floor(maxIn / f);
  const x = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let k = 0; k < f; k++) s += mono[i * f + k];
    x[i] = s / f;
  }

  const filters = melFilters(rate);
  const win = Float64Array.from({ length: FRAME }, (_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (FRAME - 1)));
  const nFrames = Math.floor(n / FRAME);
  const hop = FRAME / rate;
  const melE: Float64Array[] = [];
  const energyDb = new Float64Array(nFrames);
  const re = new Float64Array(FRAME), im = new Float64Array(FRAME);
  const dct: number[][] = Array.from({ length: N_CEP }, (_, c) => Array.from({ length: N_MEL }, (_, m) => Math.cos((Math.PI * (c + 1) * (m + 0.5)) / N_MEL)));

  for (let t = 0; t < nFrames; t++) {
    for (let i = 0; i < FRAME; i++) { re[i] = x[t * FRAME + i] * win[i]; im[i] = 0; }
    fft(re, im);
    const mag = new Float64Array(FRAME / 2);
    let total = 0;
    for (let i = 0; i < FRAME / 2; i++) { mag[i] = re[i] * re[i] + im[i] * im[i]; total += mag[i]; }
    energyDb[t] = 10 * Math.log10(total / FRAME + 1e-12);
    const eMel = new Float64Array(N_MEL);
    for (let m = 0; m < N_MEL; m++) {
      const [a, b, c] = filters[m];
      let e = 0;
      for (let i = Math.ceil(a); i <= Math.floor(c); i++) {
        const w = i <= b ? (i - a) / (b - a || 1) : (c - i) / (c - b || 1);
        if (w > 0 && i < FRAME / 2) e += w * mag[i];
      }
      eMel[m] = e;
    }
    melE.push(eMel);
  }

  // Steady background noise fills the valleys between speech sounds in every band and drags matching speech
  // apart. Estimate each band's noise from the quietest 15% of frames and subtract it (with a floor) before
  // taking logs, so a noisy copy of the same recording still looks like it.
  const sortedE = Array.from(energyDb).sort((a, b) => a - b);
  const medE = sortedE[Math.floor(sortedE.length / 2)] ?? -120;
  const p15E = sortedE[Math.floor(sortedE.length * 0.15)] ?? -120;
  // Only treat the quietest frames as noise when they really are much quieter than the typical frame; in a steady
  // tone every frame is "quiet" and subtracting would erase the sound itself.
  const noiseK = medE - p15E >= 6 ? 1.5 : 0;
  const order = Array.from({ length: nFrames }, (_, t) => t).sort((a, b) => energyDb[a] - energyDb[b]);
  const quietN = Math.max(1, Math.floor(nFrames * 0.15));
  const noise = new Float64Array(N_MEL);
  for (let k = 0; k < quietN; k++) for (let m = 0; m < N_MEL; m++) noise[m] += melE[order[k]]?.[m] / quietN;
  const cep: Float32Array[] = melE.map((eMel) => {
    const v = new Float32Array(N_CEP);
    const logMel = eMel.map((e, m) => Math.log(Math.max(e - noiseK * noise[m], 0.05 * e) + 1e-9));
    for (let c = 0; c < N_CEP; c++) { let s = 0; for (let m = 0; m < N_MEL; m++) s += dct[c][m] * logMel[m]; v[c] = s; }
    return v;
  });

  // Which frames carry sound? Louder than 32 dB below the peak and 10 dB above the quietest tenth.
  const sorted = Array.from(energyDb).sort((a, b) => a - b);
  const peak = sorted[sorted.length - 1] ?? -120;
  const p10 = sorted[Math.floor(sorted.length * 0.1)] ?? -120;
  // In a steady tone every frame is as loud as the peak, so "10 dB above the quietest tenth" would sit above the
  // peak and select nothing (and every frame, silence included, would then be used). Cap that bound at 6 dB
  // below the peak so a tone with leading silence keeps only its sounding frames.
  const thr = Math.max(peak - 32, Math.min(p10 + 10, peak - 6));
  let idx: number[] = [];
  for (let t = 0; t < nFrames; t++) if (energyDb[t] > thr) idx.push(t);
  if (idx.length < 3) idx = Array.from({ length: nFrames }, (_, t) => t);

  // Remove this file's average (volume, EQ, channel and steady noise colour).
  const mean = new Float32Array(N_CEP);
  for (const t of idx) for (let c = 0; c < N_CEP; c++) mean[c] += cep[t][c] / idx.length;
  const frames = idx.map((t) => { const v = new Float32Array(N_CEP); for (let c = 0; c < N_CEP; c++) v[c] = cep[t][c] - mean[c]; return v; });
  // Steady-sound path: keep only the 40 dB below each frame's strongest band, so the faint broadband noise that
  // lossy encoders and quantisation add to empty bands does not outweigh the tone itself.
  const raw = idx.map((t) => {
    const e = melE[t];
    const floor = Math.max(...Array.from(e)) * 1e-4 + 1e-12;
    const lm = e.map((v) => Math.log(Math.max(v, floor)));
    const v = new Float32Array(N_CEP);
    for (let c = 0; c < N_CEP; c++) { let q = 0; for (let m = 0; m < N_MEL; m++) q += dct[c][m] * lm[m]; v[c] = q; }
    return v;
  });
  let varSum = 0, totSum = 0;
  for (let k = 0; k < idx.length; k++) for (let c = 0; c < N_CEP; c++) { varSum += frames[k][c] ** 2; totSum += raw[k][c] ** 2; }
  const steady = totSum > 0 && varSum / totSum < 0.05;
  // First real sound: first 10 ms window within 40 dB of the loudest one.
  const W = Math.max(1, Math.round(rate * 0.01));
  const wins = Math.floor(n / W);
  const wr = new Float64Array(wins);
  let wmax = 0;
  for (let k = 0; k < wins; k++) { let e = 0; for (let i = 0; i < W; i++) e += x[k * W + i] ** 2; wr[k] = e / W; if (wr[k] > wmax) wmax = wr[k]; }
  let first = 0;
  while (first < wins && wr[first] < wmax * 1e-4) first++;
  const onset = first >= wins ? 0 : (first * W) / rate;
  return { frames, raw, steady, onset, times: idx.map((t) => t * hop), hop, seconds: n / rate };
}

function cosine(a: Float32Array, b: Float32Array): number {
  let d = 0, ma = 0, mb = 0;
  for (let i = 0; i < a.length; i++) { d += a[i] * b[i]; ma += a[i] * a[i]; mb += b[i] * b[i]; }
  return ma === 0 || mb === 0 ? 0 : d / Math.sqrt(ma * mb);
}

export type CellState = "match" | "differs" | "silence";

export interface Comparison {
  /** 0-100, calibrated: unrelated audio is near 0, the same content is near 100. */
  score: number;
  /** Raw mean agreement of matched frames, -1..1 (for calibration). */
  raw: number;
  /** Per-second agreement along file 1, 0..1, or null where file 1 is silent. */
  strip: Array<number | null>;
  /** The same per-second cells as `strip`, classified once here so the headline and the timeline cannot disagree. */
  cells: CellState[];
  /** Stretches (seconds in file 1) where the files do not match: runs of "differs" cells. */
  differences: Array<[number, number]>;
  /** How many one-second cells differ. */
  differingSeconds: number;
  analysedSeconds: number;
  /** Seconds that file 2's sound starts later (+) or earlier (-) than file 1's, after lining the two up. */
  offsetSeconds: number;
  /** Which features were compared. */
  mode: "shape" | "steady";
}

// Calibration: raw agreement at/below LOW is "unrelated" (0%), at/above HIGH is "the same" (100%).
export const CALIBRATION = { low: 0.4, high: 0.95 };
export const STEADY_CALIBRATION = { low: 0.6, high: 0.98 };

export function compareFeatures(a0: Features, b0: Features): Comparison {
  // Steady sounds (tones, hum) have no pattern over time to follow, so compare their spectral shape directly.
  const steady = a0.steady || b0.steady;
  const a = steady ? { ...a0, frames: a0.raw } : a0;
  const b = steady ? { ...b0, frames: b0.raw } : b0;
  const cal = steady ? STEADY_CALIBRATION : CALIBRATION;
  const n = a.frames.length, m = b.frames.length;
  const band = Math.max(8, Math.round(BAND_SECONDS / a.hop));
  const INF = 1e9, STEP_PENALTY = 0.03;
  // Cost matrix kept only inside the band around the diagonal.
  const slope = m / n;
  const lo = (i: number) => Math.max(0, Math.floor(i * slope - band));
  const hi = (i: number) => Math.min(m - 1, Math.ceil(i * slope + band));
  const cost: Float32Array[] = new Array(n);
  const back: Uint8Array[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const l = lo(i), h = hi(i), w = Math.max(0, h - l + 1);
    cost[i] = new Float32Array(w).fill(INF);
    back[i] = new Uint8Array(w);
    for (let j = l; j <= h; j++) {
      const d = 1 - cosine(a.frames[i], b.frames[j]);
      let best = INF, from = 0;
      if (i === 0 && j === 0) { best = 0; }
      if (i > 0 && j > 0) { const pj = j - 1 - lo(i - 1); if (pj >= 0 && pj < cost[i - 1].length && cost[i - 1][pj] < best) { best = cost[i - 1][pj]; from = 0; } }
      if (i > 0) { const pj = j - lo(i - 1); if (pj >= 0 && pj < cost[i - 1].length && cost[i - 1][pj] + STEP_PENALTY < best) { best = cost[i - 1][pj] + STEP_PENALTY; from = 1; } }
      if (j > l) { const pj = j - 1 - l; if (cost[i][pj] + STEP_PENALTY < best) { best = cost[i][pj] + STEP_PENALTY; from = 2; } }
      cost[i][j - l] = best >= INF ? INF : best + d;
      back[i][j - l] = from;
    }
  }
  // Trace the path back from the end.
  let i = n - 1, j = m - 1;
  const simA = new Float64Array(n), cntA = new Float64Array(n);
  const simB = new Float64Array(m), cntB = new Float64Array(m);
  const diffs: number[] = [];
  while (i >= 0 && j >= 0) {
    diffs.push(b.times[j] - a.times[i]);
    const s = cosine(a.frames[i], b.frames[j]);
    simA[i] += s; cntA[i]++; simB[j] += s; cntB[j]++;
    if (i === 0 && j === 0) break;
    const from = back[i][j - lo(i)];
    if (from === 0) { i--; j--; } else if (from === 1) { i--; } else { j--; }
    if (i < 0 || j < 0) break;
  }
  const frameA = Array.from(simA, (v, k) => (cntA[k] ? v / cntA[k] : 0));
  const frameB = Array.from(simB, (v, k) => (cntB[k] ? v / cntB[k] : 0));
  const rawA = frameA.reduce((s, v) => s + v, 0) / (n || 1);
  const rawB = frameB.reduce((s, v) => s + v, 0) / (m || 1);
  const raw = (rawA + rawB) / 2;
  const score = Math.max(0, Math.min(100, ((raw - cal.low) / (cal.high - cal.low)) * 100));

  diffs.sort((x, y) => x - y);
  const off = diffs.length ? diffs[Math.floor(diffs.length / 2)] : 0;
  // The path median is only accurate to one frame (64 ms); when it agrees with the difference between where each
  // file's sound begins, use the finer figure. A steady tone has no pattern for the path to follow, so there the
  // start of the sound is the only real evidence of an offset.
  const onsetDiff = b0.onset - a0.onset;
  const best = steady || Math.abs(off - onsetDiff) < 0.15 ? onsetDiff : off;
  const offsetSeconds = Math.abs(best) < 0.05 ? 0 : Math.round(best * 100) / 100;

  // Per-second strip along file 1 (one cell = one second; the last may be shorter).
  const secs = Math.ceil(a.seconds);
  const sum = new Float64Array(secs), cnt = new Float64Array(secs);
  for (let k = 0; k < n; k++) { const s = Math.min(secs - 1, Math.floor(a.times[k])); sum[s] += frameA[k]; cnt[s]++; }
  const strip: Array<number | null> = Array.from(sum, (v, s) => (cnt[s] >= 3 ? v / cnt[s] : null));
  const bad = (v: number | null) => v !== null && v < cal.low + 0.4 * (cal.high - cal.low);
  // Sound present in one file but not the other at the aligned position (a gap, a cut, an insert) never shows up in
  // the frame matching, because silent frames are skipped. Check it directly, per cell, against the other file
  // shifted by the offset. Cells whose partner window falls mostly outside file 2 are not judged.
  const active = (f: Features, w0: number, w1: number): { inside: number; frac: number } => {
    const lo2 = Math.max(0, w0), hi2 = Math.min(f.seconds, w1);
    const inside = Math.max(0, hi2 - lo2);
    if (inside <= 0) return { inside: 0, frac: 0 };
    let c = 0;
    for (const t of f.times) { const mid = t + f.hop / 2; if (mid >= lo2 && mid < hi2) c++; }
    return { inside, frac: Math.min(1, (c * f.hop) / inside) };
  };
  const cells: CellState[] = [];
  for (let s = 0; s < secs; s++) {
    const w0 = s, w1 = Math.min(s + 1, a.seconds);
    const A = active(a, w0, w1);
    const B = active(b, w0 + offsetSeconds, w1 + offsetSeconds);
    const judged = B.inside >= 0.5 * (w1 - w0);
    if (judged && Math.abs(A.frac - B.frac) > 0.5) { cells.push("differs"); strip[s] = 0; continue; }
    cells.push(strip[s] === null ? "silence" : bad(strip[s]) ? "differs" : "match");
  }
  const differences: Array<[number, number]> = [];
  let start = -1;
  for (let s = 0; s <= secs; s++) {
    const isBad = s < secs && cells[s] === "differs";
    if (isBad && start < 0) start = s;
    if (!isBad && start >= 0) { differences.push([start, s]); start = -1; }
  }
  const differingSeconds = cells.filter((c) => c === "differs").length;
  return { score, raw, strip, cells, differences, differingSeconds, analysedSeconds: a.seconds, offsetSeconds, mode: steady ? "steady" : "shape" };
}

export function isIdentical(a: Float32Array, b: Float32Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (Math.abs(a[i] - b[i]) > 1e-6) return false;
  return true;
}

export function describeScore(
  score: number,
  differences: Array<[number, number]>,
  identical: boolean,
  encodingDiffers = false,
  /** When given, the headline is held to what the timeline shows: no claim of differences if no second differs. */
  timeline?: { differingSeconds: number; offsetSeconds: number },
): string {
  if (identical) return "Identical audio";
  const flagged = timeline ? timeline.differingSeconds > 0 : differences.length > 0;
  if (score >= 90 && encodingDiffers && !flagged) return "Same audio, different encoding";
  if (flagged && score >= 40) return "Partly the same: most of it matches, but some parts differ";
  if (score >= 90) return timeline && timeline.offsetSeconds
    ? `Same recording; file 2 starts ${Math.abs(timeline.offsetSeconds).toFixed(2)} s ${timeline.offsetSeconds > 0 ? "later" : "earlier"}`
    : "Same recording, possibly a different format or quality";
  if (score >= 70) {
    if (!timeline || flagged) return "Same content, with some differences";
    return timeline.offsetSeconds
      ? `Same content; file 2 starts ${Math.abs(timeline.offsetSeconds).toFixed(2)} s ${timeline.offsetSeconds > 0 ? "later" : "earlier"}, no second is flagged as different`
      : "Very similar overall; no second is flagged as different";
  }
  if (score >= 40) return "Some similarities";
  return "Different audio";
}

/** "aligned by +0.50 s": file 2's sound starts that much later (+) or earlier (-) than file 1's. Empty when no shift. */
export function fmtOffset(offsetSeconds: number): string {
  if (!offsetSeconds) return "";
  return `aligned by ${offsetSeconds > 0 ? "+" : "-"}${Math.abs(offsetSeconds).toFixed(2)} s`;
}

export function fmtSeconds(s: number): string {
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}
